import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { apiRequest } from "src/services/apiClient";
import i18n from "src/i18n";
import { useApi } from "src/hooks/useApi";
import { useSorting } from "src/hooks/useSorting";
import { InvoiceFilters, DEFAULT_FILTERS, matchesOrigin, type InvoiceOwnerOption } from "./InvoiceFilterBar";
import { Invoice } from "src/types";
import { InvoiceStatus } from "src/constants/invoice";
import type { ToastState } from "src/components/Toast/Toast";
import { invoiceMatchesSearch } from "src/utils/invoiceSearch";
import BulkSendPreview from "./BulkSendPreview";
import BulkLetterPreview from "./BulkLetterPreview";
import BulkDeletePreview from "./BulkDeletePreview";
import { runBulkInBatches } from "src/utils/bulkRunner";
import { useBulkSend } from "src/context/BulkSendContext";
import { isStatusSendable, hasEmail, hasAddress, isReadyForPost, splitForBulkLetter } from "src/utils/invoiceSendability";

// Same Kasserver-backed praxis mailbox as the questionnaire send — same throttling.
const PORTAL_LINK_BATCH_SIZE = 3;
const PORTAL_LINK_BATCH_DELAY_MS = 5000;
// LetterXpress is a dedicated transactional API, not the praxis's personal mailbox — no known
// rejection issue there, so this stays a mild default rather than matching the email rate.
const LETTER_BATCH_SIZE = 5;
const LETTER_BATCH_DELAY_MS = 500;

export type InvoiceSortKey =
    | "id"
    | "recipient"
    | "amount"
    | "paid"
    | "remaining"
    | "dunning"
    | "creationDate";

export const getSortValue = (invoice: Invoice, key: InvoiceSortKey): string | number => {
    switch (key) {
        case "id":          return invoice.readableId ?? "";
        case "recipient": {
            const billed = `${invoice.patient?.lastName ?? ""} ${invoice.patient?.firstName ?? ""}`.trim();
            const account = `${invoice.recipient?.lastName ?? ""} ${invoice.recipient?.firstName ?? ""}`.trim();
            return (billed || account).toLowerCase();
        }
        case "amount":      return invoice.amount ?? 0;
        case "paid":        return invoice.paidAmount ?? 0;
        case "remaining":   return (invoice.amount ?? 0) + (invoice.dunningFineAmount ?? 0) - (invoice.paidAmount ?? 0);
        case "dunning":     return invoice.dunning ?? 0;
        case "creationDate": return new Date(invoice.creationDate).getTime();
    }
};

export { isStatusSendable, hasEmail, hasAddress, isReadyForPost };

export function useInvoiceList() {
    const location = useLocation();
    const { enqueue } = useBulkSend();

    const [search, setSearch] = useState(() => location.state?.search ?? "");
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [toast, setToast] = useState<ToastState | null>(null);
    const [filters, setFilters] = useState<InvoiceFilters>(() => ({
        ...DEFAULT_FILTERS,
        ...(location.state?.filters ?? {}),
    }));
    const [showPaid, setShowPaid] = useState(false);
    const [showWrittenOff, setShowWrittenOff] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [lxPopupInvoiceId, setLxPopupInvoiceId] = useState<string | null>(null);
    const [lxStatusJob, setLxStatusJob] = useState<Record<string, any> | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfFrameLoading, setPdfFrameLoading] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [deletePhase, setDeletePhase] = useState<"deleting" | "cleaning" | null>(null);
    const [deleteCount, setDeleteCount] = useState(0);
    const [bulkPopup, setBulkPopup] = useState<{
        title: string;
        message: React.ReactNode;
        action: () => void;
        confirmLabel: string;
        confirmVariant: "primary" | "danger";
        confirmDisabled?: boolean;
        className?: string;
    } | null>(null);

    useEffect(() => {
        if (location.state?.filters) {
            const f = location.state.filters as InvoiceFilters & { recipient?: string; patient?: string };
            setFilters((prev) => ({
                ...prev,
                ...f,
                patients: f.patients ?? f.recipient ?? f.patient ?? prev.patients,
            }));
        }
        if (location.state?.search !== undefined) setSearch(location.state.search);
        if (location.state?.refresh) setRefreshTrigger((n) => n + 1);
    }, [location.state]);

    const { sortKey, sortDir, handleSort, applySorting } = useSorting<InvoiceSortKey>();
    const { data, loading, error, refetch } = useApi<{ invoices: Invoice[]; total: number; doctors?: InvoiceOwnerOption[]; multiDoctor?: boolean }>("/invoices", refreshTrigger);
    const { data: settings } = useApi<{
        sendMethods?:  string[];
        postColor?:        boolean;
        postDuplex?:       boolean;
        postDispatch?:     string;
        postEnvelope?:     string;
        postShipping?:     string;
        questionsEnabled?:       boolean;
        requireLegalAcceptance?: boolean;
        tagsEnabled?:            boolean;
        legalMode?:              string;
        paywiseApiKey?:          string;
        paywiseConfirmed?: boolean;
        praxis?: { name?: string; street?: string; plz?: string; city?: string };
    }>("/settings");
    const postEnabled            = settings?.sendMethods?.includes("post")  ?? false;
    const emailEnabled           = settings?.sendMethods?.includes("email") ?? true;
    const questionsEnabled       = settings?.questionsEnabled !== false;
    const requireLegalAcceptance = settings?.requireLegalAcceptance !== false;
    const tagsEnabled            = settings?.tagsEnabled === true;
    const legalMode              = settings?.legalMode ?? "self";
    const hasPaywiseKey       = !!(settings?.paywiseApiKey);
    const paywiseConfirmed    = !!(settings?.paywiseConfirmed);
    const invoices = data?.invoices ?? [];
    // Owner chips + doctor filter only when the viewer sees several doctors' invoices.
    const multiDoctor   = data?.multiDoctor === true;
    const doctorOptions = multiDoctor ? (data?.doctors ?? []) : [];

    // One-shot sync on page load: check open Paywise cases for payment
    useEffect(() => {
        const hasOpenPaywiseCases = invoices.some(
            (inv) => inv.paywiseCaseId && inv.status !== 2 /* paid */,
        );
        if (!hasOpenPaywiseCases || !hasPaywiseKey) return;
        apiRequest<{ updatedIds: string[] }>("/invoices/paywise-sync")
            .then(({ updatedIds }) => { if (updatedIds.length) refetch(); })
            .catch(() => { /* silent — non-critical background sync */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [/* run once after first invoices load */ invoices.length > 0 ? "loaded" : "empty"]);

    const sendPortalLink = useCallback(async (invoiceId: string, readableId: string) => {
        try {
            await apiRequest("/patient-portal/create-link", { method: "POST", body: JSON.stringify({ invoiceId }) }, { "Content-Type": "application/json" });
            setToast({ message: i18n.t("invoicePage.toastPortalLinkSentId", { id: readableId }), variant: "success" });
            refetch();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : "";
            setToast({ message: msg === "LEGAL_NOT_ACCEPTED" ? i18n.t("invoicePage.legalNotAccepted") : (msg || i18n.t("invoicePage.toastPortalLinkFailedId", { id: readableId })), variant: "error" });
        }
    }, [refetch]);

    const viewPdf = useCallback(async (invoiceId: string) => {
        setPdfLoading(true);
        try {
            setPdfFrameLoading(true);
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/invoices/${invoiceId}/pdf-token`, {
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });
            if (!res.ok) throw new Error("Failed to prepare PDF");
            const data = (await res.json()) as { url?: string };
            if (!data?.url) throw new Error("Missing PDF URL");
            if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
            setPdfUrl(`${config.baseUrl}${data.url}`);
        } catch {
            setToast({ message: i18n.t("invoicePage.toastPdfFailed"), variant: "error" });
            setPdfFrameLoading(false);
        } finally {
            setPdfLoading(false);
        }
    }, [pdfUrl]);

    const closePdf = useCallback(() => {
        if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPdfFrameLoading(false);
    }, [pdfUrl]);

    const checkLetterStatus = useCallback(async (lxId: string) => {
        try {
            const data = await apiRequest<any>(`/notifications/letterxpress-status/${lxId}`, {}, {});
            setLxStatusJob(data?.data ?? data);
        } catch (e: unknown) {
            setToast({ message: e instanceof Error ? e.message : i18n.t("invoicePage.toastLxStatusFailed"), variant: "error" });
        }
    }, []);

    const openLxPopup = useCallback((invoiceId: string) => {
        setLxPopupInvoiceId(invoiceId);
    }, []);

    const sendViaLetterXpress = useCallback(async (invoiceId: string, color: boolean, duplex: boolean, envelope: string, dispatch: string, shipping: string) => {
        const inv = invoices.find((i) => i.id === invoiceId);
        try {
            await apiRequest(
                "/notifications/send-letterxpress",
                { method: "POST", body: JSON.stringify({ invoiceId, color, duplex, envelope, dispatch, shipping }) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: i18n.t("invoicePage.toastLxSent", { id: inv?.readableId ?? invoiceId }), variant: "success" });
            refetch();
        } catch (e: unknown) {
            setToast({ message: e instanceof Error ? e.message : i18n.t("invoicePage.toastLxFailed", { id: inv?.readableId ?? invoiceId }), variant: "error" });
        }
    }, [invoices, refetch]);

    const deleteInvoice = useCallback(async (invoiceId: string) => {
        try {
            await apiRequest(`/invoices/${invoiceId}`, { method: "DELETE" }, { "Content-Type": "application/json" });
            await refetch();
        } catch (error) {
            setToast({ message: i18n.t("invoicePage.toastDeleteFailed"), variant: "error" });
        }
    }, [refetch]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback((ids: string[]) => {
        setSelectedIds((prev) => {
            const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
            return allSelected ? new Set() : new Set(ids);
        });
    }, []);

    const isPostOnly = (inv: Invoice) => inv.patient?.postOnly ?? false;
    const isLegalBlocked = (inv: Invoice) => requireLegalAcceptance && !(inv.questionnaireAccepted ?? false);
    const isSendable = (inv: Invoice) => isStatusSendable(inv) && hasEmail(inv) && !isPostOnly(inv) && !isLegalBlocked(inv);

    const bulkSendPreview = useCallback(() => {
        const selected     = invoices.filter((i) => selectedIds.has(i.id));
        const willSend      = selected.filter((i) => isStatusSendable(i) && hasEmail(i) && !isPostOnly(i) && !isLegalBlocked(i) && !i.sentDate);
        const willResend    = selected.filter((i) => isStatusSendable(i) && hasEmail(i) && !isPostOnly(i) && !isLegalBlocked(i) && !!i.sentDate);
        const noEmail       = selected.filter((i) => isStatusSendable(i) && !hasEmail(i));
        const postOnly      = selected.filter((i) => isStatusSendable(i) && hasEmail(i) && isPostOnly(i));
        const legalBlocked  = selected.filter((i) => isStatusSendable(i) && hasEmail(i) && !isPostOnly(i) && isLegalBlocked(i));
        const cannotSend    = selected.filter((i) => !isStatusSendable(i));
        return <BulkSendPreview willSend={willSend} willResend={willResend} noEmail={noEmail} postOnly={postOnly} legalBlocked={legalBlocked} cannotSend={cannotSend} />;
    }, [selectedIds, invoices, requireLegalAcceptance]);

    const bulkSend = useCallback(() => {
        const selected = invoices.filter((i) => selectedIds.has(i.id));
        const sendable = selected.filter(isSendable);
        setBulkPopup({
            title: i18n.t("invoices.bulkSend.title", { count: selectedIds.size }),
            message: bulkSendPreview(),
            confirmLabel: `${i18n.t("invoices.send")} (${sendable.length})`,
            confirmVariant: "primary",
            confirmDisabled: sendable.length === 0,
            action: () => {
                setSelectedIds(new Set());
                enqueue(i18n.t("common.bulkSendSubjectPortalLinks"), async ({ onProgress, shouldCancel }) => {
                    const { succeeded, failed, cancelled } = await runBulkInBatches(
                        sendable,
                        PORTAL_LINK_BATCH_SIZE,
                        PORTAL_LINK_BATCH_DELAY_MS,
                        (inv) =>
                            apiRequest(
                                "/patient-portal/create-link",
                                { method: "POST", body: JSON.stringify({ invoiceId: inv.id }) },
                                { "Content-Type": "application/json" },
                            ),
                        onProgress,
                        shouldCancel,
                    );
                    if (failed.length > 0) {
                        console.error("[bulkSend] failures:", failed.map((f) => ({
                            invoiceId: f.item.id,
                            error: f.error instanceof Error ? f.error.message : f.error,
                        })));
                    }
                    const firstReason = failed[0]?.error instanceof Error ? (failed[0].error as Error).message : undefined;
                    refetch();
                    return {
                        succeeded: succeeded.length,
                        failed: failed.length,
                        cancelled,
                        message: cancelled
                            ? i18n.t("common.cancelledProgress", { done: succeeded.length + failed.length, total: sendable.length })
                            : failed.length > 0
                            ? (firstReason
                                ? i18n.t("invoices.bulkSend.donePartialReason", { sent: succeeded.length, total: sendable.length, failed: failed.length, reason: firstReason })
                                : i18n.t("invoices.bulkSend.donePartial", { sent: succeeded.length, total: sendable.length, failed: failed.length }))
                            : i18n.t("invoices.bulkSend.done", { count: succeeded.length }),
                        variant: cancelled || failed.length > 0 ? "error" : "success",
                        details: failed.map((f) => f.error instanceof Error ? f.error.message : String(f.error)),
                    };
                });
            },
        });
    }, [selectedIds, invoices, bulkSendPreview, enqueue, refetch]);

    const lxDefaults = useMemo(() => ({
        color:    settings?.postColor    ?? false,
        duplex:   settings?.postDuplex   ?? false,
        dispatch: ["r1", "r2"].includes(settings?.postDispatch ?? "") ? (settings?.postDispatch ?? "") : "",
        envelope: settings?.postEnvelope ?? "DIN_LANG_FENSTER",
        shipping: settings?.postShipping ?? "national",
    }), [settings]);

    const bulkSendLetter = useCallback(() => {
        const selected = invoices.filter((i) => selectedIds.has(i.id));
        const { canSend, noConsent, letterSent, noAddress, cannotSend } = splitForBulkLetter(selected, requireLegalAcceptance);
        const { color, duplex, envelope, dispatch, shipping } = lxDefaults;
        setBulkPopup({
            title: i18n.t("invoices.bulkLetter.title", { count: selectedIds.size }),
            message: (
                <BulkLetterPreview
                    canSend={canSend}
                    noConsent={noConsent}
                    letterSent={letterSent}
                    cannotSend={cannotSend}
                    noAddress={noAddress}
                    color={color}
                    duplex={duplex}
                    envelope={envelope}
                    dispatch={dispatch}
                    shipping={shipping}
                />
            ),
            confirmLabel: `${i18n.t("invoices.send")} (${canSend.length})`,
            confirmVariant: "primary",
            confirmDisabled: canSend.length === 0,
            className: undefined,
            action: () => {
                setSelectedIds(new Set());
                enqueue(i18n.t("common.bulkSendSubjectLetters"), async ({ onProgress, shouldCancel }) => {
                    const { succeeded, failed, cancelled } = await runBulkInBatches(
                        canSend,
                        LETTER_BATCH_SIZE,
                        LETTER_BATCH_DELAY_MS,
                        (inv) =>
                            apiRequest(
                                "/notifications/send-letterxpress",
                                { method: "POST", body: JSON.stringify({ invoiceId: inv.id, color, duplex, envelope, dispatch, shipping }) },
                                { "Content-Type": "application/json" },
                            ),
                        onProgress,
                        shouldCancel,
                    );
                    if (failed.length > 0) {
                        console.error("[bulkSendLetter] failures:", failed.map((f) => ({
                            invoiceId: f.item.id,
                            error: f.error instanceof Error ? f.error.message : f.error,
                        })));
                    }
                    const firstReason = failed[0]?.error instanceof Error ? (failed[0].error as Error).message : undefined;
                    refetch();
                    return {
                        succeeded: succeeded.length,
                        failed: failed.length,
                        cancelled,
                        message: cancelled
                            ? i18n.t("common.cancelledProgress", { done: succeeded.length + failed.length, total: canSend.length })
                            : failed.length > 0
                            ? (firstReason
                                ? i18n.t("invoices.bulkLetter.donePartialReason", { sent: succeeded.length, total: canSend.length, failed: failed.length, reason: firstReason })
                                : i18n.t("invoices.bulkLetter.donePartial", { sent: succeeded.length, total: canSend.length, failed: failed.length }))
                            : i18n.t("invoices.bulkLetter.done", { count: succeeded.length }),
                        variant: cancelled || failed.length > 0 ? "error" : "success",
                        details: failed.map((f) => f.error instanceof Error ? f.error.message : String(f.error)),
                    };
                });
            },
        });
    }, [selectedIds, invoices, lxDefaults, requireLegalAcceptance, enqueue, refetch]);

    const isDeletable = (inv: Invoice) =>
        !inv.sentDate && !(inv.dunning ?? 0) && inv.status !== InvoiceStatus.PAID && inv.status !== InvoiceStatus.WRITTEN_OFF;

    const bulkDelete = useCallback(() => {
        const selected     = invoices.filter((i) => selectedIds.has(i.id));
        const canDelete    = selected.filter(isDeletable);
        const cannotDelete = selected.filter((i) => !isDeletable(i));
        setBulkPopup({
            title: i18n.t("invoices.bulkDelete.title", { count: selectedIds.size }),
            message: <BulkDeletePreview canDelete={canDelete} cannotDelete={cannotDelete} />,
            confirmLabel: `${i18n.t("invoices.delete")} (${canDelete.length})`,
            confirmVariant: "danger",
            confirmDisabled: canDelete.length === 0,
            action: async () => {
                const ids = canDelete.map((i) => i.id);
                setSelectedIds(new Set());
                setBulkDeleting(true);
                setDeleteCount(ids.length);
                setDeletePhase("deleting");
                try {
                    await Promise.all(ids.map((id) =>
                        apiRequest(`/invoices/${id}`, { method: "DELETE" }, { "Content-Type": "application/json" }).catch(() => {}),
                    ));
                    setDeletePhase("cleaning");
                } finally {
                    await refetch();
                    setBulkDeleting(false);
                    setDeletePhase(null);
                }
            },
        });
    }, [selectedIds, invoices, refetch]);

    const SERVER_SIDE_THRESHOLD = 5000;
    const PAGE_SIZES = [10, 25, 50, 100] as const;
    const [pageSize, setPageSize] = useState<number>(50);
    const [page, setPage] = useState(0);

    // Switch to server-side mode once we know total > threshold
    const [serverMode, setServerMode] = useState(false);
    useEffect(() => {
        if ((data?.total ?? 0) > SERVER_SIDE_THRESHOLD) setServerMode(true);
    }, [data?.total]);

    // In server-side mode, build the URL with all filter params so useApi refetches on change
    const serverUrl = useMemo(() => {
        if (!serverMode) return null;
        const params = new URLSearchParams({
            page:     String(page + 1),
            pageSize: String(pageSize),
        });
        if (search)              params.set("search",      search);
        if (filters.status)      params.set("status",      filters.status);
        if (filters.dunning)     params.set("dunning",     filters.dunning);
        if (filters.sent !== "all") params.set("sent",     filters.sent);
        if (filters.amountMin)   params.set("amountMin",   filters.amountMin);
        if (filters.patients)    params.set("patients",    filters.patients);
        if (filters.tag)         params.set("tag",         filters.tag);
        if (filters.doctor)      params.set("doctor",      filters.doctor);
        if (showPaid)          params.set("showPaid",    "1");
        if (showWrittenOff)      params.set("showWrittenOff", "1");
        return `/invoices?${params.toString()}`;
    }, [serverMode, page, pageSize, search, filters, showPaid, showWrittenOff]);

    const { data: serverData, loading: serverLoading } = useApi<{ invoices: Invoice[]; total: number; filteredTotal: number }>(
        serverUrl,
        refreshTrigger,
    );

    const filteredInvoices = useMemo(() => applySorting(
        invoices
            .filter((i) => {
                // If a status chip is explicitly selected, let all statuses through and
                // let the status filter below do the narrowing
                if (filters.status !== "") return true;
                if (showPaid) return i.status === InvoiceStatus.PAID;
                if (showWrittenOff) return i.status === InvoiceStatus.WRITTEN_OFF;
                return i.status !== InvoiceStatus.PAID && i.status !== InvoiceStatus.WRITTEN_OFF;
            })
            .filter((i) => invoiceMatchesSearch(i, search))
            .filter((i) => {
                if (filters.sent !== "all") {
                    const isSent = !!i.sentDate;
                    if (filters.sent === "sent" && !isSent) return false;
                    if (filters.sent === "unsent" && isSent) return false;
                }
                if (filters.amountMin !== "" && i.amount < parseFloat(filters.amountMin)) return false;
                if (filters.dunning !== "" && String(i.dunning) !== filters.dunning) return false;
                if (filters.status !== "" && String(i.status) !== filters.status) return false;
                if (filters.patients) {
                    const q = filters.patients.toLowerCase();
                    const account = `${i.recipient?.firstName ?? ""} ${i.recipient?.lastName ?? ""}`.toLowerCase();
                    const billed = `${i.patient?.firstName ?? ""} ${i.patient?.lastName ?? ""}`.toLowerCase();
                    if (!account.includes(q) && !billed.includes(q)) return false;
                }
                if (filters.createdFrom) {
                    const from = new Date(filters.createdFrom);
                    from.setHours(0, 0, 0, 0);
                    if (new Date(i.creationDate) < from) return false;
                }
                if (filters.createdTo) {
                    const to = new Date(filters.createdTo);
                    to.setHours(23, 59, 59, 999);
                    if (new Date(i.creationDate) > to) return false;
                }
                // postConsentAccepted, not questionnaireAccepted — physical/postal Einwilligung
                // consent counts as accepted too, same as the backend CSV export's
                // resolveQuestionnaireRecipientIds (invoiceController.ts).
                if (filters.questionnaireSubmitted && !i.postConsentAccepted) return false;
                // "not submitted" = link was sent but patient hasn't given any consent yet
                if (filters.questionnaireNotSubmitted && !(i.questionnaireSent && !i.postConsentAccepted)) return false;
                // "not sent" = no questionnaire activity at all (no link sent, no consent given)
                if (filters.questionnaireNotSent && (i.questionnaireSent || i.postConsentAccepted)) return false;
                if (filters.vatOnly && !((i.vatRate ?? 0) > 0)) return false;
                if (filters.readyForPost && !isReadyForPost(i, requireLegalAcceptance)) return false;
                if (filters.tag && !i.tagIds?.includes(filters.tag)) return false;
                if (filters.doctor && i.ownerDoctorId !== filters.doctor) return false;
                if (!matchesOrigin(filters.origin, i.manualInvoice)) return false;
                return true;
            }),
        getSortValue,
    ), [invoices, search, filters, sortKey, sortDir, showPaid, showWrittenOff, requireLegalAcceptance]);

    // When a status chip is selected, clear the toggle buttons so they don't conflict
    useEffect(() => {
        if (filters.status !== "") { setShowPaid(false); setShowWrittenOff(false); }
    }, [filters.status]);

    // Reset to page 0 whenever filters / search change
    useEffect(() => { setPage(0); }, [search, filters, showPaid, showWrittenOff, pageSize]);

    // In server mode: the server handles filtering — use its results directly
    const displayInvoices  = serverMode ? (serverData?.invoices ?? []) : filteredInvoices;
    const displayTotal     = serverMode ? (serverData?.filteredTotal ?? 0) : filteredInvoices.length;
    const totalPages       = Math.max(1, Math.ceil((serverMode ? displayTotal : filteredInvoices.length) / pageSize));
    const safePage         = Math.min(page, totalPages - 1);
    const pagedInvoices    = serverMode
        ? displayInvoices  // server already returned the correct page
        : filteredInvoices.slice(safePage * pageSize, (safePage + 1) * pageSize);

    const paidCount       = invoices.filter((i) => i.status === InvoiceStatus.PAID).length;
    const writtenOffCount = invoices.filter((i) => i.status === InvoiceStatus.WRITTEN_OFF).length;

    return {
        search, setSearch,
        toast, setToast,
        filters, setFilters,
        showPaid, setShowPaid,
        showWrittenOff, setShowWrittenOff,
        paidCount,
        writtenOffCount,
        selectedIds, setSelectedIds,
        bulkPopup, setBulkPopup,
        loading: serverMode ? serverLoading : loading, error,
        filteredInvoices: displayInvoices,
        pagedInvoices,
        page: safePage, setPage,
        pageSize, setPageSize,
        totalPages,
        PAGE_SIZES,
        serverMode,
        sortKey, sortDir, handleSort,
        postEnabled,
        emailEnabled,
        questionsEnabled,
        requireLegalAcceptance,
        tagsEnabled,
        legalMode,
        hasPaywiseKey,
        paywiseConfirmed,
        invoices,
        multiDoctor,
        doctorOptions,
        lxPopupInvoiceId, setLxPopupInvoiceId,
        lxStatusJob, setLxStatusJob, checkLetterStatus,
        lxPraxis: settings?.praxis,
        lxDefaults,
        pdfUrl, pdfLoading, pdfFrameLoading, setPdfFrameLoading, closePdf,
        sendPortalLink, openLxPopup, viewPdf, sendViaLetterXpress, deleteInvoice,
        toggleSelect, toggleSelectAll,
        bulkSend, bulkSendLetter, bulkDelete, bulkDeleting, deletePhase, deleteCount,
        refetch,
    };
}
